import { useMemo } from 'react';
import {
    LocalStateFieldWithKeyAndName,
    useLocalStateFields,
    UseLocalStateFieldsProps,
} from '@hooks-clean/use-local-state-fields';

type UseLocalStateFieldsMappedProps<TResult> = UseLocalStateFieldsProps & {
    mapFn: (field: LocalStateFieldWithKeyAndName) => TResult;
};

/**
 * Filters local state fields based on parameters.
 *
 * Fields are always filtered out if they are null or if either of their `key`/`name` properties are null
 *
 * - `mapFn` - maps field to some other type
 * - `fieldTypes` - (optional). which field types to return. If omitted, all field types are returned
 *   - If defined, this should be memoized
 * - `dataTypes` - (optional). which data types to return. If omitted, all data types are returned
 *   - If defined, this should be memoized
 * - `isHidden` - (optional). whether to return hidden or non-hidden fields. If omitted, both are returned
 * - `isCalculated` - (optional). whether to return calculated or non-calculated fields. If omitted, both are returned
 */
export function useLocalStateFieldsMapped<TResult>({
    mapFn,
    ...props
}: UseLocalStateFieldsMappedProps<TResult>): TResult[] {
    const fields = useLocalStateFields(props);

    return useMemo(() => fields.map(mapFn), [fields, mapFn]);
}
